/**
 * Web Storage Service Implementation
 * Uses localStorage for browser storage
 * Falls back to memory storage if localStorage unavailable (private browsing)
 */

import type { StorageService } from './storage.base';
import { StorageHelpers } from './storage.base';

class WebStorageService implements StorageService {
  private memoryStorage: Map<string, string> = new Map();
  private useMemoryFallback = false;

  constructor() {
    // Check if localStorage is available
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (e) {
      console.warn('[WebStorage] localStorage not available, using memory fallback');
      this.useMemoryFallback = true;
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      if (this.useMemoryFallback) {
        return this.memoryStorage.get(key) ?? null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`[WebStorage] Error getting item "${key}":`, error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      if (this.useMemoryFallback) {
        this.memoryStorage.set(key, value);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`[WebStorage] Error setting item "${key}":`, error);
      // If quota exceeded, try memory fallback
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('[WebStorage] Quota exceeded, switching to memory storage');
        this.useMemoryFallback = true;
        this.memoryStorage.set(key, value);
      } else {
        throw error;
      }
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      if (this.useMemoryFallback) {
        this.memoryStorage.delete(key);
      } else {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`[WebStorage] Error removing item "${key}":`, error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.useMemoryFallback) {
        this.memoryStorage.clear();
      } else {
        localStorage.clear();
      }
    } catch (error) {
      console.error('[WebStorage] Error clearing storage:', error);
      throw error;
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      if (this.useMemoryFallback) {
        return Array.from(this.memoryStorage.keys());
      }
      return Object.keys(localStorage);
    } catch (error) {
      console.error('[WebStorage] Error getting all keys:', error);
      return [];
    }
  }

  async multiGet(keys: string[]): Promise<Array<[string, string | null]>> {
    try {
      return keys.map(key => [key, this.getItemSync(key)]);
    } catch (error) {
      console.error('[WebStorage] Error in multiGet:', error);
      return keys.map(key => [key, null]);
    }
  }

  async multiSet(keyValuePairs: Array<[string, string]>): Promise<void> {
    try {
      for (const [key, value] of keyValuePairs) {
        await this.setItem(key, value);
      }
    } catch (error) {
      console.error('[WebStorage] Error in multiSet:', error);
      throw error;
    }
  }

  async multiRemove(keys: string[]): Promise<void> {
    try {
      for (const key of keys) {
        await this.removeItem(key);
      }
    } catch (error) {
      console.error('[WebStorage] Error in multiRemove:', error);
      throw error;
    }
  }

  // Sync helper for multiGet
  private getItemSync(key: string): string | null {
    try {
      if (this.useMemoryFallback) {
        return this.memoryStorage.get(key) ?? null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
const storageService = new WebStorageService();
export default storageService;

// Export helpers
export const storageHelpers = new StorageHelpers(storageService);
